import { useEffect, useState, type ReactNode } from "react";
import { colorHex } from "../lib/colors";
import type { PlayerView } from "../protocol";

export function Avatar({ player, size = 36 }: { player: Pick<PlayerView, "name" | "color">; size?: number }) {
  return (
    <span
      className="avatar"
      aria-hidden="true"
      style={{ background: colorHex(player.color), width: size, height: size, fontSize: size * 0.45 }}
    >
      {player.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Seconds left until a server deadline, ticking with the synced clock. */
export function useCountdown(deadline: number | undefined, serverNow: () => number): number {
  const [left, setLeft] = useState(() => (deadline ? Math.max(0, deadline - serverNow()) : 0));
  useEffect(() => {
    if (!deadline) return setLeft(0);
    const update = () => setLeft(Math.max(0, deadline - serverNow()));
    update();
    const t = window.setInterval(update, 250);
    return () => window.clearInterval(t);
  }, [deadline, serverNow]);
  return Math.ceil(left / 1000);
}

export function Timer({ deadline, serverNow, total }: { deadline?: number; serverNow: () => number; total: number }) {
  const secs = useCountdown(deadline, serverNow);
  const pct = total > 0 ? Math.min(100, (secs / total) * 100) : 0;
  const urgent = secs <= 10;
  // Announce only the milestones, not every second (TDD: Accessibility).
  const announce = secs === 30 || secs === 10 ? `${secs} seconds left` : "";
  return (
    <div className={`timer${urgent ? " urgent" : ""}`} role="timer">
      <div className="timer-bar" style={{ width: `${pct}%` }} />
      <span className="timer-num" aria-hidden="true">{secs}</span>
      <span className="sr-only" aria-live="polite">{announce}</span>
    </div>
  );
}

export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = window.setTimeout(onDone, 3500);
    return () => window.clearTimeout(t);
  }, [message, onDone]);
  return (
    <div className="toast" role="status">
      {message}
    </div>
  );
}

export function Sheet({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 className="sheet-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}
